/**
 * @module @fanini/shared-api/queries/public
 * Query Factory fuer oeffentliche Endpunkte (keine Auth erforderlich).
 *
 * @example
 * ```tsx
 * const queries = createPublicQueries('https://api.fanini.live');
 * const { data } = useQuery(queries.events());
 * const { data } = useQuery(queries.event('event-123'));
 * ```
 */
import { queryOptions } from '@tanstack/react-query';
import type { Event } from '@fanini/shared-types';
import { ApiRequestError } from '../client';

/**
 * Fuehrt einen oeffentlichen GET-Request aus (ohne Auth-Token).
 *
 * @param url - Vollstaendige URL fuer den Request
 * @returns Geparste JSON-Response
 * @throws ApiRequestError bei nicht-2xx Antworten
 */
async function publicFetch<T>(url: string): Promise<T> {
  const res = await fetch(url);

  if (!res.ok) {
    let message = `HTTP ${res.status}: ${res.statusText}`;
    try {
      const errBody = (await res.json()) as { message?: string; error?: string };
      message = errBody.message ?? errBody.error ?? message;
    } catch {
      // Fehler-Body konnte nicht geparst werden — Fallback auf HTTP-Status
    }
    throw new ApiRequestError(res.status, message);
  }

  return res.json() as Promise<T>;
}

/**
 * Erstellt eine Query Factory fuer oeffentliche API-Endpunkte.
 * Diese Queries benoetigen keinen Auth-Token.
 *
 * @param baseUrl - Basis-URL der API (z.B. "https://api.fanini.live")
 * @returns Public Query Factory
 */
export function createPublicQueries(baseUrl: string) {
  const base = baseUrl.replace(/\/$/, '');

  return {
    /**
     * Root-Query-Key fuer alle Public-Queries.
     */
    all: () => ['public'] as const,

    /**
     * Query fuer die oeffentliche Event-Liste.
     * Kein Auth-Token erforderlich.
     *
     * @returns TanStack Query Options
     */
    events: () =>
      queryOptions({
        queryKey: ['public', 'events'] as const,
        queryFn: (): Promise<Event[]> => publicFetch<Event[]>(`${base}/api/public/events`),
        staleTime: 60_000, // oeffentliche Daten — 1 Minute stale time
      }),

    /**
     * Query fuer ein einzelnes oeffentliches Event anhand der ID.
     * Kein Auth-Token erforderlich.
     *
     * @param id - Event-ID
     * @returns TanStack Query Options
     */
    event: (id: string) =>
      queryOptions({
        queryKey: ['public', 'events', id] as const,
        queryFn: (): Promise<Event> => publicFetch<Event>(`${base}/api/public/events/${id}`),
        staleTime: 60_000,
      }),
  };
}

/** Typ der Public Query Factory */
export type PublicQueries = ReturnType<typeof createPublicQueries>;

/**
 * @deprecated Verwende stattdessen `createPublicQueries(baseUrl)` mit einer konfigurierbaren Base-URL.
 * Dieses statische Objekt nutzt eine leere Base-URL (relative Pfade).
 * Wird in einer kuenftigen Version entfernt.
 *
 * @example
 * ```tsx
 * // Vorher (deprecated):
 * import { publicQueries } from '@fanini/shared-api';
 * useQuery(publicQueries.events());
 *
 * // Nachher:
 * import { createPublicQueries } from '@fanini/shared-api';
 * const queries = createPublicQueries('https://api.fanini.live');
 * useQuery(queries.events());
 * ```
 */
export const publicQueries: PublicQueries = createPublicQueries('');
